import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import Computer from 'mbs-ui-shared@models/Computer';
import {
  AgentRemoteCommand,
  AgentRemoteCommandType,
  BulkRemoteCommand,
  BulkRemoteCommandType,
  GroupRemoteCommand,
  RemoteCommandPayload,
  RemoteCommandResponse,
  RepositorySyncState,
  SingleRemoteCommand
} from 'mbs-ui-shared@models/rm/remote-command-model';
import { AsyncRemoteCommandService, getRemoteCommandUrl } from 'mbs-ui-shared@services/async-remote-command/async-remote-command.service';
import { HttpClientMethodOptions, Params } from 'mbs-ui-shared@services/async-remote-command/async-remote-command.types';
import { Observable } from 'rxjs';

const apiGroupRemoteCommand = 'api/computers/groupremotecommand';

/**
 * Remote Management commands
 * https://utrack.cloudberrylab.com/articles/BACKUP-A-6/RM-API
 */
@Injectable({ providedIn: 'root' })
export class RmCommandsService {
  private http = inject(HttpClient);
  private asyncRemoteCommandService = inject(AsyncRemoteCommandService);

  remoteCommandByHid<TResult, TResponse = RemoteCommandResponse<TResult>>(
    hid: string,
    payload: RemoteCommandPayload,
    async?: boolean,
    options?: HttpClientMethodOptions
  ): Observable<TResponse> {
    if (async) {
      return this.asyncRemoteCommandService.runAsyncCommand({
        hid,
        params: payload as unknown as Params,
        options
      });
    }

    return this.http.post<TResponse>(getRemoteCommandUrl(hid, payload.commandType), payload, options);
  }

  remoteCommand<TResult>(computer: Computer, payload: SingleRemoteCommand): Observable<RemoteCommandResponse<TResult>> {
    return this.remoteCommandByHid(computer.hid, payload);
  }

  groupRemoteCommand<TCommands extends AgentRemoteCommand<AgentRemoteCommandType>, TResult>(
    payload: GroupRemoteCommand<TCommands>,
    async?: boolean
  ): Observable<TResult> {
    if (async) {
      return this.asyncRemoteCommandService.runGroupAsyncCommand<TCommands, TResult>({
        url: apiGroupRemoteCommand,
        params: payload
      });
    }

    return this.http.post<TResult>(apiGroupRemoteCommand, payload);
  }

  bulkRemoteCommand<TParams extends BulkRemoteCommandType, TResult>(
    payload: BulkRemoteCommand<TParams>,
    async?: boolean
  ): Observable<TResult> {
    if (async) {
      return this.asyncRemoteCommandService.runBulkAsyncCommand<TParams, TResult>({
        url: 'api/computers/bulkremotecommand',
        requestIdUrl: apiGroupRemoteCommand,
        params: payload
      });
    }

    return this.http.post<TResult>('api/computers/bulkremotecommand', payload);
  }

  // installAgent and uninstallAgents -> remote commands!
  uninstallAgents(payload: { hid: string; appIds: string }): Observable<any> {
    return this.http.delete<any>(`api/computers/${payload.hid}/applications?${payload.appIds}`);
  }

  getSyncStatus(hid: string): Observable<{ state: RepositorySyncState }> {
    return this.http.get<any>(`api/computers/${hid}/repository-sync/state`);
  }
}
